/**
 * Small dependency-free UI kit. Every component maps onto class names defined
 * in styles.css so visual changes stay in one place.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

/* ---------- brand ---------- */

export function DropLogo({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <path d="M16 3C16 3 6 14.5 6 21a10 10 0 0 0 20 0C26 14.5 16 3 16 3Z" fill="var(--accent)" />
      <path
        d="M12 21a4.5 4.5 0 0 0 4.5 4.5"
        stroke="var(--panel)"
        strokeWidth="2.2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

/* ---------- buttons ---------- */

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'ghost' | 'subtle' | 'danger';
  size?: 'md' | 'sm';
  busy?: boolean;
  block?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  busy = false,
  block = false,
  className,
  children,
  disabled,
  type,
  ...rest
}: ButtonProps) {
  const cls = ['btn', variant, size === 'sm' ? 'sm' : '', block ? 'block' : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <button className={cls} type={type ?? 'button'} disabled={disabled || busy} {...rest}>
      {busy && <span className="spinner" aria-hidden="true" />}
      {children}
    </button>
  );
}

/* ---------- form fields ---------- */

let fieldSeq = 0;

/**
 * The id of the current Field's hint/error, or undefined when it has neither.
 * Lets TextInput/Select wire `aria-describedby` correctly without every one of
 * the ~55 call sites having to thread it through by hand.
 */
const DescribedByContext = createContext<string | undefined>(undefined);

interface FieldProps {
  label: string;
  hint?: ReactNode;
  error?: string;
  children: (id: string, invalid: boolean, describedBy: string) => ReactNode;
}

/**
 * Label + control + hint/error, wired together for accessibility.
 *
 * The hint/error carries `${id}-desc`, and TextInput/Select point
 * `aria-describedby` at it automatically, so a screen-reader user hears "At
 * least 8 characters" or the reason a value was rejected — not just "invalid".
 * The id is referenced unconditionally: a reference to an element that isn't
 * there is ignored by assistive tech, and that costs far less than threading a
 * third argument through all ~55 call sites.
 */
export function Field({ label, hint, error, children }: FieldProps) {
  const [id] = useState(() => `f${++fieldSeq}`);
  const descId = `${id}-desc`;
  const described = error || hint ? descId : undefined;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <DescribedByContext.Provider value={described}>{children(id, Boolean(error), descId)}</DescribedByContext.Provider>
      {error ? (
        <span className="field-error" id={descId}>
          {error}
        </span>
      ) : hint ? (
        <span className="hint" id={descId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  const { className, id, ...rest } = props;
  const describedBy = useContext(DescribedByContext);
  return (
    <input id={id} className={`input ${className ?? ''}`} aria-describedby={describedBy} {...rest} />
  );
}

interface SelectProps<T extends string> {
  id?: string;
  value: T;
  options: readonly T[];
  labels?: Partial<Record<T, string>>;
  onChange: (value: T) => void;
}

export function Select<T extends string>({ id, value, options, labels, onChange }: SelectProps<T>) {
  const describedBy = useContext(DescribedByContext);
  return (
    <select
      id={id}
      className="input"
      aria-describedby={describedBy}
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {labels?.[o] ?? o}
        </option>
      ))}
    </select>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  hideLabel = false,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  /**
   * Keep the accessible name but don't paint it. For rows where a heading above
   * already says the same words — several Settings toggles rendered their name
   * twice, once as a heading with a description and once beside the switch.
   */
  hideLabel?: boolean;
  disabled?: boolean;
}) {
  return (
    <label className="switch">
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
      />
      <span className="track" aria-hidden="true" />
      {label && !hideLabel && <span>{label}</span>}
    </label>
  );
}

/* ---------- surfaces ---------- */

export function Card({
  title,
  sub,
  right,
  children,
  className,
}: {
  title?: string;
  sub?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card ${className ?? ''}`}>
      {(title || right) && (
        <div className="card-head">
          <div>
            {title && <h2>{title}</h2>}
            {sub && <div className="sub">{sub}</div>}
          </div>
          {right}
        </div>
      )}
      {children}
    </section>
  );
}

export function Pill({
  tone = 'muted',
  plain = false,
  children,
}: {
  tone?: 'good' | 'warn' | 'bad' | 'accent' | 'muted';
  plain?: boolean;
  children: ReactNode;
}) {
  return <span className={`pill ${tone}${plain ? ' plain' : ''}`}>{children}</span>;
}

/**
 * A message strip. Prose flows normally; pass `action` for a trailing control
 * (a Dismiss button, say) and the row becomes a flex layout with the text
 * growing to fill.
 *
 * `.banner` is deliberately NOT flex by default. It used to be, and because
 * most banners are written as a sentence containing a <Link> — three sibling
 * nodes, not one — flex made each an anonymous item and laid them out side by
 * side, so the sentence rendered torn in half with the link stranded in its own
 * column.
 */
export function Banner({
  tone,
  action,
  children,
}: {
  tone: 'error' | 'ok' | 'info';
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={`banner ${tone}${action ? ' has-action' : ''}`} role={tone === 'error' ? 'alert' : 'status'}>
      {action ? (
        <>
          <div className="grow">{children}</div>
          {action}
        </>
      ) : (
        children
      )}
    </div>
  );
}

export function Skeleton({ height = '1rem', width = '100%' }: { height?: string; width?: string }) {
  return <div className="skeleton" style={{ height, width }} aria-hidden="true" />;
}

/** A card-shaped loading placeholder used while a page's data loads. */
export function CardSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="card stack" role="status" aria-busy="true" aria-label="Loading">
      <Skeleton width="35%" height="1.1rem" />
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} width={`${88 - i * 14}%`} />
      ))}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  actions,
}: {
  icon?: string;
  title: string;
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="empty">
      {icon && (
        <div className="icon" aria-hidden="true">
          {icon}
        </div>
      )}
      <div className="title">{title}</div>
      {children && <p>{children}</p>}
      {actions && <div className="actions">{actions}</div>}
    </div>
  );
}

/**
 * A titled group of form fields.
 *
 * `collapsible` renders it as a native <details> with a one-line summary of
 * what's inside, so settings that are derived from other choices and touched
 * about once ever don't compete for attention with the ones people revisit.
 */
export function Section({
  title,
  summary,
  collapsible = false,
  defaultOpen = false,
  children,
}: {
  title: string;
  summary?: ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  if (!collapsible) {
    return (
      <section className="form-section">
        <h3>{title}</h3>
        <div className="stack">{children}</div>
      </section>
    );
  }
  return (
    <details className="form-section collapsible" open={defaultOpen}>
      <summary>
        <h3>{title}</h3>
        {summary && <span className="section-summary">{summary}</span>}
      </summary>
      <div className="stack">{children}</div>
    </details>
  );
}

/* ---------- overflow menu ---------- */

export interface MenuItem {
  label: string;
  onSelect: () => void;
  /** Renders in the destructive tone and is separated from the rest. */
  danger?: boolean;
  /** Draws attention — used for advisories the zone is currently raising. */
  emphasis?: boolean;
}

/**
 * A "⋯" button revealing a short list of secondary actions.
 *
 * Exists so a zone card can show its two common actions at full size instead of
 * six equal-weight ones that wrapped differently on every card. Implements the
 * menu-button keyboard contract: Enter/Space/ArrowDown open and focus the first
 * item, Escape closes and returns focus to the button, arrows move, Tab or an
 * outside click closes.
 */
export function Menu({ label, items }: { label: string; items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    // Focus the first item so the keyboard path works without a second key.
    listRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();

    const onDocDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        btnRef.current?.focus();
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
      const list = [...(listRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
      if (list.length === 0) return;
      e.preventDefault();
      const i = list.indexOf(document.activeElement as HTMLElement);
      const next =
        e.key === 'Home'
          ? 0
          : e.key === 'End'
            ? list.length - 1
            : e.key === 'ArrowDown'
              ? (i + 1) % list.length
              : (i - 1 + list.length) % list.length;
      list[next]?.focus();
    };
    // Tabbing away is a deliberate exit, not a trap — close and let focus go.
    const onFocusOut = () => {
      window.setTimeout(() => {
        if (!wrapRef.current?.contains(document.activeElement)) setOpen(false);
      }, 0);
    };

    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    wrapRef.current?.addEventListener('focusout', onFocusOut);
    const wrap = wrapRef.current;
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
      wrap?.removeEventListener('focusout', onFocusOut);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div className="menu-wrap" ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        className="btn ghost sm menu-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open && (
        <div className="menu-list" role="menu" id={menuId} ref={listRef} aria-label={label}>
          {items.map((item, i) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={`menu-item${item.danger ? ' danger' : ''}${item.emphasis ? ' emphasis' : ''}${
                item.danger && i > 0 ? ' divide' : ''
              }`}
              onClick={() => {
                // Focus the trigger BEFORE acting: if the action opens a dialog,
                // that dialog records whatever has focus as the element to
                // return to, and this menu item is about to unmount.
                btnRef.current?.focus();
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- modal ---------- */

/** Everything that can take keyboard focus, in DOM order. */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function Modal({
  title,
  sub,
  onClose,
  narrow = false,
  children,
}: {
  title: string;
  sub?: ReactNode;
  onClose: () => void;
  narrow?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const headingId = useId();
  const subId = useId();

  // Callers pass an inline arrow, so `onClose` is a new function every render.
  // Holding it in a ref keeps the effect below mount-only — depending on it
  // directly would re-run the effect (and its focus restore) on every parent
  // render, yanking focus back out of the open dialog.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Where focus came from, so it can be handed back on close — without this,
  // Escape drops focus to <body> and the user restarts from the top of the page.
  //
  // Captured in a ref initializer, which runs during the FIRST RENDER. Reading
  // it in the effect below would be too late: React applies a child's
  // `autoFocus` during commit, so by then the "opener" would already be an
  // input inside this very dialog, and closing would restore focus to a
  // detached node (measured — RunNow lands on <body> that way).
  const openerRef = useRef<HTMLElement | null>(
    typeof document === 'undefined' ? null : (document.activeElement as HTMLElement | null),
  );

  useEffect(() => {
    const dialog = ref.current;
    const opener = openerRef.current;

    // Move focus to the dialog itself, so a screen reader announces its name and
    // description before any control.
    //
    // Deliberately NOT "focus the first control": that would land on the Close
    // button, and deliberately not "respect a child's autoFocus" either —
    // React strips the autofocus attribute and calls focus() imperatively during
    // commit, which races this passive effect (measured: the same dialog landed
    // on the input in one run and on Close in the next). Focusing the container
    // is deterministic, and Tab from here reaches the first control.
    dialog?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') return onCloseRef.current();
      if (e.key !== 'Tab' || !dialog) return;

      // Trap focus. Without this, Tab walks the page behind the overlay — on the
      // Zones page that means the invisible Delete button of every other zone,
      // which Enter would happily activate.
      const items = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
      if (items.length === 0) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    // Prevent the page behind the modal from scrolling.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      // A dialog that deleted the row it was opened from has no trigger left to
      // return to, so fall back to the main region — otherwise focus lands on
      // <body> and a keyboard or screen-reader user restarts from the top.
      if (opener?.isConnected) opener.focus();
      else document.getElementById('main')?.focus();
    };
  }, []);

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        if (e.target !== e.currentTarget) return;
        // Suppress mousedown's default focus change. Without it the browser
        // moves focus to <body> after the unmount hands focus back to the
        // opener, so a backdrop-click close would silently lose the trigger.
        e.preventDefault();
        onClose();
      }}
    >
      <div
        className={`modal${narrow ? ' narrow' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={sub ? subId : undefined}
        tabIndex={-1}
        ref={ref}
      >
        <div className="modal-head">
          <div>
            <h2 id={headingId}>{title}</h2>
            {sub && (
              <div className="sub" id={subId}>
                {sub}
              </div>
            )}
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ---------- toasts ---------- */

interface Toast {
  id: number;
  text: string;
  tone: 'ok' | 'error';
}

const ToastContext = createContext<(text: string, tone?: 'ok' | 'error') => void>(() => {});

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);

  const push = useCallback((text: string, tone: 'ok' | 'error' = 'ok') => {
    const id = ++seq.current;
    setToasts((t) => [...t, { id, text, tone }]);
    // Errors linger: they carry a reason the reader has to act on, and a
    // dismissed toast can't be brought back. Confirmations can go quickly.
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === 'error' ? 10_000 : 4000);
  }, []);

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast${t.tone === 'error' ? ' error' : ''}`}
            onClick={() => setToasts((list) => list.filter((x) => x.id !== t.id))}
          >
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
